import { AxiosError, AxiosResponse } from 'axios';
import moment from 'moment';
import { UseMutationOptions, UseMutationResult } from 'react-query';

import useAPIMutation from '../api/useAPIMutation';
import {
  CandidateDetail,
  SerialID,
  ExtendedCountryPhoneInputValue,
  CandidateDetailData,
  CandidateDisabilityCollection,
} from '../types';

const maxFileSizeInBytes = 2_097_152; // 2Mb
interface UseSaveCandidateMutationValues {
  id: CandidateDetail['id'];
  name?: string;
  typeGender?: NonNullable<CandidateDetail['gender']>;
  email?: string;
  birthday?: string | null;
  phoneContacts?: ExtendedCountryPhoneInputValue[];
  linkedInProfile?: string;
  salaryExpectation?: number;
  candidateDisabilitiesComplement?: {
    disability: CandidateDisabilityCollection['type'];
    cid?: string;
  }[];
  professionalGoal?: string;
  professionalSummary?: string;
  languages?: CandidateDetail['languages'];
  knowledges?: {
    id?: SerialID;
    description: string;
  }[];
  abilities?: {
    id?: SerialID;
    description: string;
  }[];
  personalInterests?: {
    id?: SerialID;
    interest: string;
  }[];
  cpf?: string;
  rg?: string;
  address?: {
    postalCode?: string;
    additional?: string;
    number?: string;
    street?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
    country?: string;
  };
  typeMaritalStatus?: string;
  travelPossibility?: boolean;
  realocationPossibility?: boolean;
  professionalExperiences?: {
    id?: SerialID;
    address?: string;
    companyName: string;
    description?: string;
    jobPositionName: string;
    startDate?: string;
    endDate?: string;
    currentlyWorks?: boolean;
  }[];
  education?: {
    id?: SerialID;
    courseName: string;
    conclusionYear?: string;
    institutionName: string;
    typeEducationLevel: string;
    typeSituation: string;
  }[];
  attachment?: File | null;
  portfolio?: string;
  tags?: number[];
}

type UseSaveCandidateMutation = (
  options?: UseMutationOptions<
    AxiosResponse<CandidateDetail>,
    unknown,
    UseSaveCandidateMutationValues,
    unknown
  >
) => UseMutationResult<
  AxiosResponse<CandidateDetail>,
  AxiosError,
  UseSaveCandidateMutationValues,
  unknown
>;

const useSaveCandidateMutation: UseSaveCandidateMutation = (options?) => {
  const mutation = useAPIMutation(
    async (
      {
        id,
        name,
        email,
        birthday,
        typeGender,
        salaryExpectation,
        professionalGoal,
        professionalSummary,
        phoneContacts,
        candidateDisabilitiesComplement,
        linkedInProfile,
        languages,
        knowledges,
        abilities,
        personalInterests,
        cpf,
        rg,
        address,
        professionalExperiences,
        education,
        typeMaritalStatus,
        realocationPossibility,
        travelPossibility,
        attachment,
        portfolio,
        tags,
      }: UseSaveCandidateMutationValues,
      api
    ) => {
      const formDisabilityTypes = candidateDisabilitiesComplement?.map(
        (complement) => complement.disability
      );

      const mergedCandidate: Partial<CandidateDetailData> = {
        name,
        portfolio,
        abilities: (abilities || []).map(({ id, description }) => ({ id, description })),
        address_postal_code: address?.postalCode,
        address_additional: address?.additional,
        address_number: address?.number,
        address_street: address?.street,
        address_administrative_region: address?.neighborhood,
        address_city: address?.city ?? null,
        address_state: address?.state ?? null,
        address_country: address?.country ?? null,
        disabilities:
          formDisabilityTypes && formDisabilityTypes.length > 0 ? formDisabilityTypes : null,
        education: (education || []).map(
          ({
            id,
            courseName,
            conclusionYear,
            institutionName,
            typeEducationLevel,
            typeSituation,
          }) =>
            ({
              id,
              course_name: courseName,
              conclusion_year: Number(conclusionYear),
              institution_name: institutionName,
              type_education_level: typeEducationLevel,
              type_situation: typeSituation,
            } as CandidateDetail['education'][number])
        ),
        knowledge: (knowledges || []).map(({ id, description }) => ({ id, description })),
        languages: (languages || []).map(({ id, language, proficiency }) => ({
          id,
          language,
          proficiency,
        })),
        personal_interests: (personalInterests || []).map(({ id, interest }) => ({ id, interest })),
        phone_contacts: phoneContacts
          ? phoneContacts.map(
              ({ id, phoneNumber: { phone: number, code: countryCode }, phoneType }) => {
                return {
                  id,
                  number,
                  country_code: countryCode ?? 55,
                  type: phoneType,
                } as CandidateDetail['phone_contacts'][number];
              }
            )
          : [],
        professional_experiences: (professionalExperiences || []).map(
          ({
            id,
            address,
            companyName,
            description,
            jobPositionName,
            startDate,
            endDate,
            currentlyWorks,
          }) => {
            return {
              id,
              address,
              company_name: companyName,
              description,
              job_position_name: jobPositionName,
              start_date: moment(startDate).format('YYYY-MM-DD'),
              end_date: currentlyWorks === true ? null : moment(endDate).format('YYYY-MM-DD'),
            };
          }
        ),
        rg: rg ?? '',
        social_networks:
          linkedInProfile !== undefined
            ? [
                {
                  profile: linkedInProfile,
                  type: 'LINKEDIN',
                },
              ]
            : [],
        cpf: cpf ? cpf?.replaceAll(/[^\w\s]/g, '') : null,
        email: email?.toLowerCase(),
        birthdate: birthday ? moment(birthday).format('YYYY-MM-DD') : null,
        gender: typeGender,
        professional_goal: professionalGoal,
        professional_summary: professionalSummary,
        salary_expectation: salaryExpectation,
        travel_possibility: travelPossibility,
        marital_status: typeMaritalStatus as CandidateDetail['marital_status'],
        realocation_possibility: realocationPossibility,
        tags,
      };

      if (attachment || attachment === null) {
        const formData = new FormData();
        formData.append('attachment', attachment ?? '');
        return await api.patch(`/candidates/${id}/`, formData, {
          maxBodyLength: maxFileSizeInBytes,
        });
      }

      const existingCandidateDisabilities = await api.get('/candidate-disability/', {
        params: { candidate: id },
      });

      const existingDisabilityTypes = existingCandidateDisabilities.data.results.map(
        (disability) => disability.type
      );

      let disabilitiesToCreate = formDisabilityTypes;

      if (existingDisabilityTypes && existingDisabilityTypes.length) {
        const disabilitiesToUpdate = existingDisabilityTypes.filter((disability) =>
          formDisabilityTypes?.includes(disability)
        );

        disabilitiesToCreate = formDisabilityTypes?.filter(
          (disability) => !existingDisabilityTypes?.includes(disability)
        );

        const disabilitiesToRemove = existingDisabilityTypes.filter(
          (disability) => !formDisabilityTypes?.includes(disability)
        );

        // Update Candidate Disability
        for (const disabilityType of disabilitiesToUpdate) {
          const disabilityObject = existingCandidateDisabilities.data.results.find(
            (disabilityObject) => disabilityObject.type === disabilityType
          );

          const disabilityComplement = candidateDisabilitiesComplement?.find(
            (complement) => complement.disability === disabilityType
          );

          if (disabilityObject) {
            await api.patch(`/candidate-disability/${disabilityObject.id}/`, {
              cid: disabilityComplement?.cid,
            });
          }
        }

        // Remove Candidate Disability
        for (const disabilityType of disabilitiesToRemove) {
          const disabilityObject = existingCandidateDisabilities.data.results.find(
            (disabilityObject) => disabilityObject.type === disabilityType
          );

          if (disabilityObject) {
            await api.delete(`/candidate-disability/${disabilityObject.id}/`);
          }
        }
      }

      // Create Candidate Disability
      if (disabilitiesToCreate && disabilitiesToCreate.length > 0) {
        for (const disabilityType of disabilitiesToCreate) {
          const disabilityComplement = candidateDisabilitiesComplement?.find(
            (complement) => complement.disability === disabilityType
          );

          if (disabilityComplement) {
            await api.post('/candidate-disability/', {
              candidate: id,
              type: disabilityComplement.disability,
              cid: disabilityComplement.cid,
            });
          }
        }
      }

      return await api.patch(`/candidates/${id}/`, mergedCandidate);
    },
    options
  );

  return mutation;
};

export default useSaveCandidateMutation;
